import { translate } from "../localization";
import API from "./API";
import ExperiencesAPI from "./ExperiencesAPI";

export const FEATURED_COLLECTION_ID = "6739a511-3981-4b13-9ba0-68524af90775";
const COLLECTION_ITEM_TYPE_EXPERIENCE = 1;

const featuredCollectionCache = new Map();
const experienceDetailsCache = new Map();
const systemDetailsCache = new Map();

const getCachedRequest = (cache, key, requestFn) => {
  if (!key) {
    return Promise.resolve(null);
  }

  if (cache.has(key)) {
    return cache.get(key);
  }

  const requestPromise = Promise.resolve()
    .then(requestFn)
    .catch((error) => {
      cache.delete(key);
      throw error;
    });

  cache.set(key, requestPromise);
  return requestPromise;
};

const getCollectionPayload = (responseData) => responseData?.data || responseData || {};
const getExperiencePayload = (responseData) => responseData?.data || responseData || {};
const getSystemPayload = (responseData) => responseData?.data || responseData || {};

const getCardTitle = (item) =>
  item?.name || item?.title || item?.metadata?.name || translate("Untitled Experience");

const getNumericValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
};

const getFirstNumericValue = (...values) => {
  for (const value of values) {
    const numericValue = getNumericValue(value);
    if (numericValue !== null) {
      return numericValue;
    }
  }

  return null;
};

const getCollectionItemOrder = (item) =>
  getFirstNumericValue(item?.order, item?.metadata?.order);

const getExperienceItems = (collectionPayload) => {
  const items = Array.isArray(collectionPayload?.items) ? collectionPayload.items : [];

  return items
    .filter((item) => Number(item?.type) === COLLECTION_ITEM_TYPE_EXPERIENCE && item?.id)
    .sort((a, b) => {
      const aOrder = getCollectionItemOrder(a);
      const bOrder = getCollectionItemOrder(b);

      if (aOrder === null && bOrder === null) {
        return getCardTitle(a).localeCompare(getCardTitle(b));
      }

      if (aOrder === null) {
        return 1;
      }

      if (bOrder === null) {
        return -1;
      }

      if (aOrder === bOrder) {
        return getCardTitle(a).localeCompare(getCardTitle(b));
      }

      return aOrder - bOrder;
    });
};

const getFirstTextValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const fetchFeaturedCollection = async () => {
  const payload = await getCachedRequest(
    featuredCollectionCache,
    FEATURED_COLLECTION_ID,
    async () => {
      const response = await API.get(`collections/${FEATURED_COLLECTION_ID}`, {
        requiresAuth: true,
      });
      return getCollectionPayload(response?.data);
    }
  );

  return payload || {};
};

const fetchExperienceDetails = async (experienceId) => {
  if (!experienceId) {
    return null;
  }

  try {
    return await getCachedRequest(experienceDetailsCache, experienceId, async () => {
      const response = await ExperiencesAPI.get(`experiences/${experienceId}`, {
        requiresAuth: true,
      });
      return getExperiencePayload(response?.data);
    });
  } catch {
    return null;
  }
};

const fetchSystemDetails = async (systemId) => {
  if (!systemId) {
    return null;
  }

  try {
    return await getCachedRequest(systemDetailsCache, systemId, async () => {
      const response = await API.get(`systems/${systemId}`, {
        requiresAuth: true,
      });
      return getSystemPayload(response?.data);
    });
  } catch {
    return null;
  }
};

export const fetchFeaturedExperienceTemplates = async () => {
  const payload = await fetchFeaturedCollection();
  const experienceItems = getExperienceItems(payload);

  const templates = await Promise.all(
    experienceItems.map(async (item) => {
      const experienceDetails = await fetchExperienceDetails(item.id);
      const systemId = getFirstTextValue(
        experienceDetails?.systemId,
        experienceDetails?.metadata?.systemId,
        item?.systemId
      );
      const guideId = getFirstTextValue(
        experienceDetails?.guideId,
        experienceDetails?.metadata?.guideId,
        item?.guideId
      );
      const systemDetails = systemId ? await fetchSystemDetails(systemId) : null;

      return {
        experienceId: item.id,
        title: getCardTitle(item),
        systemId,
        guideId,
        systemName: getFirstTextValue(
          systemDetails?.name,
          systemDetails?.metadata?.name
        ),
      };
    })
  );

  return templates.filter((template) => template.systemId || template.guideId);
};
